/**
 * Manifest export contracts — Module 4 (TW-3449).
 *
 * A trip's manifest (the `trip_passenger` ⋈ `passenger` set, data-model §6) is exported to CSV and
 * PDF for airlines/rail/hotels. The bytes are generated in the worker and returned to the browser
 * via a short-lived signed URL; this leaf is the single source of truth both ends share — the API
 * validates the request with the Zod schema, the worker builds the CSV with the pure builder here,
 * and the web client renders the same column labels.
 *
 * The CSV maths is a **pure, clock-free fold** (like the other domain summaries): given the flat
 * passenger rows + the localized labels it produces deterministic text, so the column order and
 * RFC-4180 escaping are unit-testable without a database or a worker.
 *
 * Note on PII: unlike the audit snapshot (which deliberately omits the document *number*), the
 * export **includes the full document number** — it is the airline/hotel-bound deliverable whose
 * whole purpose is to convey identity-document details. Access is controlled by the signed-URL gate
 * (short-lived, unguessable), never by withholding the field.
 */
import { z } from "zod";

/** The export formats a client may request (CSV for systems, PDF for a printable manifest). */
export const MANIFEST_EXPORT_FORMATS = ["csv", "pdf"] as const;
export type ManifestExportFormat = (typeof MANIFEST_EXPORT_FORMATS)[number];

/** Request body for `POST /api/trips/:tripId/passengers/export`. */
export const requestManifestExportSchema = z.object({
  format: z.enum(MANIFEST_EXPORT_FORMATS),
});
export type RequestManifestExportInput = z.infer<typeof requestManifestExportSchema>;

/** One manifest row, flattened from a `trip_passenger` join with its `passenger` (date `YYYY-MM-DD`). */
export interface ManifestExportPassenger {
  fullName: string;
  isStaff: boolean;
  roleOnTrip: string | null;
  nationality: string | null;
  documentType: string | null;
  documentNumber: string | null;
  documentExpiry: string | null;
}

/**
 * The ordered export columns. The keys map to localized header strings
 * (`manifest.export.columns.<key>`) and fix the cell order in every row, so the CSV header and
 * body always line up and the PDF table mirrors it.
 */
export const MANIFEST_EXPORT_COLUMNS = [
  "name",
  "role",
  "roleOnTrip",
  "nationality",
  "docType",
  "docNumber",
  "docExpiry",
] as const;
export type ManifestExportColumn = (typeof MANIFEST_EXPORT_COLUMNS)[number];

/** Resolved, localized strings the byte builders need (headers + the player/staff role words). */
export interface ManifestExportLabels {
  columns: Record<ManifestExportColumn, string>;
  player: string;
  staff: string;
}

/**
 * Map one passenger to its ordered cells (column order = {@link MANIFEST_EXPORT_COLUMNS}). Pure.
 * Includes the full document number — see the file header on why the export carries the PII the
 * audit snapshot withholds.
 */
export function manifestExportRow(p: ManifestExportPassenger, labels: ManifestExportLabels): string[] {
  return [
    p.fullName,
    p.isStaff ? labels.staff : labels.player,
    p.roleOnTrip ?? "",
    p.nationality ?? "",
    p.documentType ?? "",
    p.documentNumber ?? "",
    p.documentExpiry ?? "",
  ];
}

/** Escape one CSV cell per RFC-4180: quote it when it contains a comma, quote or newline. */
function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * Build the full manifest CSV (a header row followed by one row per passenger), CRLF-terminated per
 * RFC-4180. Pure + deterministic — the worker prepends a UTF-8 BOM when it turns this into bytes so
 * Excel renders accented Spanish names correctly; the BOM is a byte-level concern kept out of here.
 */
export function buildManifestCsv(rows: ManifestExportPassenger[], labels: ManifestExportLabels): string {
  const header = MANIFEST_EXPORT_COLUMNS.map((c) => labels.columns[c]);
  const lines = [header, ...rows.map((r) => manifestExportRow(r, labels))];
  return lines.map((cells) => cells.map(csvCell).join(",")).join("\r\n");
}

/** A generated export document as surfaced to the client (the `document` row, minus the storage key). */
export interface ManifestDocumentDto {
  id: string;
  kind: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
}

/** The response to a queued export request — poll `GET .../export/:exportId` with this id. */
export interface ManifestExportQueuedDto {
  exportId: string;
  format: ManifestExportFormat;
}

/**
 * The export's current state. `pending` while the worker is still generating (or the job is
 * queued); `ready` once the document exists, carrying a freshly-signed, short-lived download URL.
 */
export interface ManifestExportStatusDto {
  status: "pending" | "ready";
  document?: ManifestDocumentDto;
  downloadUrl?: string;
  expiresAt?: string;
}
